import { Injectable } from "@angular/core";
import { memoizeSelector } from "@obg/ngx.global";
import { compact, map as lodashMap, uniq } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { Game } from "../game/game.model";
import { GameSelector } from "../game/game.selector";
import { mapGames } from "../game/game.util";
import { GamingExternalState } from "../state";
import { isArrayEqualShallow } from "../utils/comparisons/comparisons";

import { GameCollection, GameCollectionRaw, GameCollectionState } from "./game-collection.model";

// @dynamic
@Injectable({
	providedIn: "root",
})
export class GameCollectionSelector {
	constructor(private gameSelector: GameSelector) {}

	getAll() {
		return (state: GamingExternalState): Record<string, GameCollectionRaw> =>
			state.gamingExternal.gameCollection.gameCollections;
	}

	getState() {
		return (state: GamingExternalState): GameCollectionState => state.gamingExternal.gameCollection;
	}

	getRawById(id: string) {
		return (state: GamingExternalState): GameCollectionRaw | undefined =>
			state.gamingExternal.gameCollection.gameCollections[id];
	}

	getById(id: string) {
		return (state: GamingExternalState): GameCollection => {
			const collection = state.gamingExternal.gameCollection.gameCollections[id];

			return (
				collection &&
				Object.assign({}, collection, {
					games: mapGames(collection.games, this.gameSelector.getAll()(state)),
				})
			);
		};
	}

	getById$(id: string) {
		return (state$: Observable<GamingExternalState>): Observable<GameCollection> =>
			state$.pipe(
				distinctUntilChanged(isArrayEqualShallow, (state) => [
					state.gamingExternal.gameCollection.gameCollections,
					this.gameSelector.getAll()(state),
				]),
				map(this.getById(id))
			);
	}

	@memoizeSelector<GamingExternalState>((state) => [state.gamingExternal.gameCollection.gameCollections])
	getByIds(ids: string[]) {
		return (state: GamingExternalState): GameCollection[] => lodashMap(ids, (id) => this.getById(id)(state));
	}

	@memoizeSelector<GamingExternalState>((state) => [state.gamingExternal.gameCollection.gameCollections])
	getRootIdById(id: string) {
		return (state: GamingExternalState): string => {
			const collections = state.gamingExternal.gameCollection.gameCollections;
			let collection = collections[id];

			while (collection?.parentId) {
				collection = collections[collection.parentId];
			}

			return collection?.id;
		};
	}

	@memoizeSelector<GamingExternalState>(undefined, () => [GameCollectionSelector.prototype.getRootIdById])
	getRootIdsByIds(ids: string[], unique = true) {
		return (state: GamingExternalState): string[] => {
			const rootIds = lodashMap(ids, (id) => this.getRootIdById(id)(state));
			return unique ? uniq(compact(rootIds)) : rootIds;
		};
	}

	getBySlug(slug: string) {
		return (state: GamingExternalState): GameCollection => {
			const id = state.gamingExternal.gameCollection.slugs[slug];

			return this.getById(id)(state);
		};
	}

	getBySlug$(slug: string) {
		return (state$: Observable<GamingExternalState>): Observable<GameCollection> =>
			state$.pipe(
				distinctUntilChanged(isArrayEqualShallow, (state) => [
					state.gamingExternal.gameCollection.gameCollections,
					this.gameSelector.getAll()(state),
				]),
				map(this.getBySlug(slug))
			);
	}

	getGamesById(id: string) {
		return (state: GamingExternalState): Game[] => this.getById(id)(state)?.games;
	}

	getGamesById$(id: string) {
		return (state$: Observable<GamingExternalState>): Observable<Game[]> =>
			state$.pipe(
				distinctUntilChanged(isArrayEqualShallow, (state) => [
					state.gamingExternal.gameCollection.gameCollections,
					this.gameSelector.getAll()(state),
				]),
				map(this.getGamesById(id))
			);
	}

	getGamesBySlug(slug: string) {
		return (state: GamingExternalState): Game[] => this.getBySlug(slug)(state)?.games;
	}

	getGamesBySlug$(slug: string) {
		return (state$: Observable<GamingExternalState>): Observable<Game[]> =>
			state$.pipe(
				distinctUntilChanged(isArrayEqualShallow, (state) => [
					state.gamingExternal.gameCollection.gameCollections,
					this.gameSelector.getAll()(state),
				]),
				map(this.getGamesBySlug(slug))
			);
	}

	getSubCollectionsById(id: string) {
		return (state: GamingExternalState): GameCollection[] => {
			const collection = state.gamingExternal.gameCollection.gameCollections[id];

			return (
				collection && lodashMap(collection.subCollections, (collectionId) => this.getById(collectionId)(state))
			);
		};
	}

	getSubCollectionsById$(id: string) {
		return (state$: Observable<GamingExternalState>): Observable<GameCollection[]> =>
			state$.pipe(
				distinctUntilChanged(isArrayEqualShallow, (state) => [
					state.gamingExternal.gameCollection.gameCollections,
					this.gameSelector.getAll()(state),
				]),
				map(this.getSubCollectionsById(id))
			);
	}

	isBusy() {
		return (state: GamingExternalState): boolean => state.gamingExternal.gameCollection.isBusy;
	}

	getGameIdsByCollectionId(collectionId: string) {
		return (state: GamingExternalState): string[] => {
			const collection = state.gamingExternal.gameCollection.gameCollections[collectionId];
			return collection?.games;
		};
	}
}
